/**
 * 공통 아이콘 컴포넌트
 * 중복 인라인 정의 대신 이 파일에서 import해서 사용하세요.
 *
 * 신규 아이콘은 목업(`remote-files/safetyhub_*.html`)의 lucide 계열 규약을 따른다:
 * `viewBox 0 0 24 24 · fill none · stroke currentColor · stroke-width 2 · 둥근 캡`.
 * 아래 `STROKE` 상수를 spread해서 쓰면 된다.
 *
 * 크기는 호출처가 `className`(`h-4 w-4` 등)으로 정한다 — SVG라 크기 클래스가 실제로 먹는다.
 * (과거 글꼴 글리프 span으로 대체돼 있던 시절에는 크기 클래스가 무시됐다.)
 */
import type { ComponentType } from 'react';

export interface IconProps {
  className?: string;
}

/**
 * 아이콘 슬롯의 표준 타입 — `icon` prop을 받는 컴포넌트는 **이 타입으로 좁혀서** 받는다.
 *
 * `ReactNode`로 넓히면 호출부가 `icon: '✓'` 같은 문자열 글리프를 계속 넘길 수 있어
 * 리스킨이 되돌아온다(글꼴 글리프는 크기 클래스도 안 먹는다). 컴포넌트만 받으면
 * 렌더 쪽이 `className`으로 치수를 정하고, 새 호출부가 글리프를 넣는 순간 tsc가 막는다.
 */
export type IconComponent = ComponentType<IconProps>;

/** 목업 SVG 공통 속성 — stroke 2, 둥근 캡. */
const STROKE = {
  fill: 'none',
  viewBox: '0 0 24 24',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

// ── 기존 heroicons 계열 (호출처 다수 — 형태 유지) ──────────────

export function CalendarIcon({ className }: IconProps) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    </svg>
  );
}

export function ChevronLeftIcon({ className }: IconProps) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
  );
}

export function ChevronRightIcon({ className }: IconProps) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}

export function ChevronDownIcon({ className }: IconProps) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

export function ChevronUpIcon({ className }: IconProps) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
    </svg>
  );
}

export function ClockIcon({ className }: IconProps) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

// ── 보고서 유형 픽토그램 (결재함 타일 · 유형 배지) ──────────────

/** 안전점검 — 방패. */
export function ShieldIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

/** TBM — 체크 박스. */
export function CheckSquareIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE}>
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  );
}

/** 위험성평가 · 경고 — 경고 삼각형. */
export function AlertTriangleIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE}>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <path d="M12 9v4M12 17h.01" />
    </svg>
  );
}

/** 기술지도 — 점검표. 빈 목록 안내에도 쓴다. */
export function ClipboardCheckIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE}>
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <rect x="8" y="2" width="8" height="4" rx="1" />
      <path d="m9 14 2 2 4-4" />
    </svg>
  );
}

/** 불이행 통보서 — 경고 팔각형. */
export function AlertOctagonIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE}>
      <path d="M7.86 2h8.28L22 7.86v8.28L16.14 22H7.86L2 16.14V7.86z" />
      <path d="M12 8v4M12 16h.01" />
    </svg>
  );
}

/** 확인요청서 — 돋보기. */
export function SearchIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE}>
      <circle cx="11" cy="11" r="7" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

/** 완료증명서 — 수여 메달. */
export function AwardIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE}>
      <circle cx="12" cy="8" r="6" />
      <path d="M15.5 13.5 17 22l-5-3-5 3 1.5-8.5" />
    </svg>
  );
}

/** TBM 증빙 — 사진. */
export function PhotoIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE}>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="9" cy="9" r="2" />
      <path d="m21 15-4.35-4.35a2 2 0 0 0-2.83 0L6 19" />
    </svg>
  );
}

/**
 * 사진 촬영 — 카메라. 시안 점검 빈 상태(`.empty-illus`)의 일러스트.
 * `PhotoIcon`(이미지 액자)과 달리 "찍어서 올린다"는 행동을 가리킨다.
 */
export function CameraIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE} strokeWidth={1.6}>
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}

/** 일일안전점검일지 — 문서. */
export function DocumentIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
    </svg>
  );
}

// ── 메타행 · 스텝퍼 ──────────────────────────────────────────

/** 현장 — 지도 핀. */
export function MapPinIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE}>
      <path d="M20 10c0 7-8 12-8 12s-8-5-8-12a8 8 0 0 1 16 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

/** 체크 표시 — 완료·선택됨. */
export function CheckIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE} strokeWidth={3}>
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

/** X 표시 — 닫기·반려. */
export function XIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE} strokeWidth={2.4}>
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

/** 진행 중 단계 — 굵은 시계(스텝퍼 전용. 일반 시계는 `ClockIcon`). */
export function StepClockIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE} strokeWidth={2.4}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  );
}

/** 미도달 단계 — 빈 원. */
export function CircleIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE} strokeWidth={2.4}>
      <circle cx="12" cy="12" r="10" />
    </svg>
  );
}

// ── 액션 · 상태 ──────────────────────────────────────────────

/** 추가 — 플러스. */
export function PlusIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

/** 취소·오류 — 원 안의 X. */
export function XCircleIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE}>
      <circle cx="12" cy="12" r="10" />
      <path d="m15 9-6 6M9 9l6 6" />
    </svg>
  );
}

/** 더보기 — 가로 점 셋. */
export function EllipsisHorizontalIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="5" cy="12" r="1.75" />
      <circle cx="12" cy="12" r="1.75" />
      <circle cx="19" cy="12" r="1.75" />
    </svg>
  );
}

/** 되돌리기 — U턴 화살표. */
export function ArrowUturnLeftIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE}>
      <path d="M9 14 4 9l5-5" />
      <path d="M4 9h11a5 5 0 0 1 0 10H9" />
    </svg>
  );
}

/** 재시도·재분석 — 회전 화살표. */
export function ArrowPathIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE}>
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
      <path d="M21 3v6h-6" />
    </svg>
  );
}

/** 수정 — 연필. */
export function PencilIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" />
    </svg>
  );
}

/** 삭제 — 휴지통. */
export function TrashIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE}>
      <path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  );
}

/** 상세 보기 — 눈. */
export function EyeIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE}>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

/** 인쇄·PDF — 프린터. */
export function PrinterIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE}>
      <path d="M6 9V3h12v6" />
      <path d="M6 18H4a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-2" />
      <rect x="6" y="14" width="12" height="7" rx="1" />
    </svg>
  );
}

/** 공유 — 내보내기 화살표. */
export function ShareIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE}>
      <path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7" />
      <path d="M16 6l-4-4-4 4M12 2v14" />
    </svg>
  );
}

/** 다운로드 — 아래 화살표 + 받침. */
export function ArrowDownTrayIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE}>
      <path d="M12 3v12M8 11l4 4 4-4" />
      <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
    </svg>
  );
}

/** 안내 — 원 안의 i. */
export function InfoIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4M12 8h.01" />
    </svg>
  );
}

/** 알 수 없음 — 원 안의 물음표. 매핑에 없는 상태의 fallback. */
export function HelpCircleIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE}>
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <path d="M12 17h.01" />
    </svg>
  );
}

/** 사람 한 명 — 현장 담당자 등 단일 인물. */
export function UserIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE}>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

/** 사람 여럿 — 점검 담당자 등 복수 인물. */
export function UsersIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

/** 정렬 가능 표시 — 위아래 화살표. 정렬 열 머리글의 기본(미정렬) 상태. */
export function ArrowsUpDownIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE}>
      <path d="M7 3v18M3 7l4-4 4 4" />
      <path d="M17 21V3M13 17l4 4 4-4" />
    </svg>
  );
}

/** 모래시계 — 처리 중(스피너와 함께 쓴다). */
export function HourglassIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE}>
      <path d="M6 2h12M6 22h12" />
      <path d="M8 2v4a4 4 0 0 0 4 4 4 4 0 0 0 4-4V2" />
      <path d="M8 22v-4a4 4 0 0 1 4-4 4 4 0 0 1 4 4v4" />
    </svg>
  );
}

/** 건물 — 멤버 관리 '담당 현장' 필터(목업 `.filter-dd` 아이콘). */
export function BuildingIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE}>
      <path d="M3 21h18M5 21V7l8-4v18M19 21V11l-6-4" />
    </svg>
  );
}

/** 길이가 줄어드는 세 줄 — 문서관리 '유형' 필터(목업 `.filter-dd` 아이콘). */
export function ListLinesIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE}>
      <path d="M22 7H2M5 12h14M9 17h6" />
    </svg>
  );
}

/**
 * 원 안의 체크 — 문서관리 '상태' 필터(목업 `.filter-dd` 아이콘).
 * `CheckSquareIcon`(네모)과 그림이 다르다: 이쪽은 상태 축을 가리키는 필터 글리프다.
 */
export function CheckCircleIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE}>
      <circle cx="12" cy="12" r="10" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

/** 활동 맥박 곡선 — 멤버 관리 '평균 활동도' 카드(목업 `.stat.draft` 아이콘). */
export function ActivityIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE}>
      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
    </svg>
  );
}

/** 진행 중 표시 — 채운 점(맥박 애니메이션과 함께 쓴다). */
export function DotIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="12" cy="12" r="5" />
    </svg>
  );
}

/**
 * 외부 링크(새 창) — 이모지 `↗`를 대체한다.
 * `ShareIcon`(위로 내보내는 상자)과 뜻이 다르다: 이쪽은 "여기서 나가서 저기로 간다"다.
 */
export function ExternalLinkIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE}>
      <path d="M14 4h6v6M20 4l-8 8" />
      <path d="M18 14v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4" />
    </svg>
  );
}

/**
 * 잠김(자물쇠) — 「여기서는 바꿀 수 없다」는 표식.
 *
 * 소비처는 둘이고 **같은 뜻**이다: 권한 매트릭스의 잠긴 키(`orgAdjustable=false` — 시스템 역할 값이
 * 항상 이긴다)와 결재라인 편집 모달의 잠긴 칸(양식이 고정한 단계). 그래서 한 자리에 둔다 —
 * 종전엔 두 파일이 같은 path를 각자 인라인으로 들고 있었다.
 *
 * 장식이라 `aria-hidden` — 뜻은 옆 문구·`title`이 말한다(자물쇠만 읽어 주면 왜 잠겼는지 알 수 없다).
 */
export function LockIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE} aria-hidden="true">
      <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  );
}

/**
 * 개선 대책 — 이모지 `🛠️`를 대체한다(공구/렌치).
 * `ShieldIcon`(예방)·`CheckSquareIcon`(완료)과 구분되는 "손을 대어 고친다"는 뜻이다.
 */
export function WrenchIcon({ className }: IconProps) {
  return (
    <svg className={className} {...STROKE}>
      <path d="M14.7 6.3a4 4 0 0 0 5 5l-8.4 8.4a2.1 2.1 0 0 1-3-3z" />
      <path d="M14.7 6.3 17.5 3.5a4 4 0 0 1 3 3l-2.8 2.8" />
    </svg>
  );
}
